import { AsyncPipe, NgClass } from '@angular/common';
import { Component, computed, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NgScrollbar } from 'ngx-scrollbar';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { CashFlowGraphComponent } from '../../../framework/cash-flow/cash-flow-graph/cash-flow-graph.component';
import { CashflowTableComponent } from '../../../framework/cash-flow/cashflow-table/cashflow-table.component';
import { specialCharReplaceRegexp } from '../../../framework/constants/spend.constants';
import { GeneralHeaderComponent } from '../../../framework/general-header/general-header.component';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../framework/inputs/dropdown/dropdown.component';
import { PageLoadingComponent } from '../../../framework/page-loading/page-loading.component';
import { SearchInputComponent } from '../../../framework/search-input/search-input.component';
import { CashFlowService } from '../../../services/cash-flow.service';
import { FiscalService } from '../../../services/fiscal.service';
import { cashflowActions } from '../../../store/cashflow/cashflow.actions';
import { cashflowSelectors } from '../../../store/cashflow/cashflow.selectors';
import { projectActions } from '../../../store/projects/projects.actions';
import { selectNonDeletedSimpleProjects } from '../../../store/projects/projects.selectors';
import { propertiesActions } from '../../../store/properties/properties.actions';
import { IProperty } from '../../../store/properties/properties.interfaces';
import { propertiesSelectors } from '../../../store/properties/properties.selectors';
import { viewProjectSelectors } from '../../../store/view-project/view-project.selectors';
import { ISimpleProject } from '../projects/projects.interface';
import { CASHFLOW_VIEWS } from './cash-flow.constants';
import { CurrentUserService } from '@app/services/current-user.service';
import { DeepCopyService } from '@app/services/deep-copy.service';
import {
  CASHFLOW_STATUS,
  CASHFLOW_TYPES,
} from '@app/framework/cash-flow/cashflow-table/cashflow.types';

@Component({
  selector: 'app-cash-flow',
  templateUrl: './cash-flow.component.html',
  styleUrls: ['./cash-flow.component.scss'],
  imports: [
    CashFlowGraphComponent,
    CashflowTableComponent,
    NgScrollbar,
    PageLoadingComponent,
    NgClass,
    AsyncPipe,
    DropdownComponent,
    GeneralHeaderComponent,
    SearchInputComponent,
    FormsModule,
  ],
})
export class CashFlowComponent implements OnInit, OnDestroy {
  readonly CASHFLOW_STATUS = CASHFLOW_STATUS;
  cashflowStatus = toSignal(this.store.select(cashflowSelectors.selectCashflowStatus));

  fiscalYear$ = this.fiscalService.fiscalYear$;
  fiscalYears$ = this.cashflowService.getFiscalYearFilterOptions$();
  selectedProjectId$ = this.store.select(viewProjectSelectors.selectProjectId);

  filters = toSignal(this.store.select(cashflowSelectors.selectCashflowFilters));

  @Input() isViewProject: boolean;
  isDestroyed$ = new Subject<boolean>();
  properties$ = this.store.select(propertiesSelectors.getAllProperties);

  projects$ = this.store.select(selectNonDeletedSimpleProjects);

  allProjects: ISimpleProject[] = []; // all possibly selectable projects
  projectOptions: DropdownOption<ISimpleProject>[] = []; // project options filtered based on properties

  propertyOptions: DropdownOption<IProperty>[] = [];

  readonly cashflowViewOptions: DropdownOption<undefined>[] = [
    {
      value: CASHFLOW_VIEWS.COMMITMENTS,
      label: 'Commitments',
    },
    {
      value: CASHFLOW_VIEWS.PRIME_CONTRACT,
      label: 'Prime Contract',
    },
    {
      value: CASHFLOW_VIEWS.PRIME_V_COMMIT,
      label: 'Prime v Commit',
    },
  ];

  cashflowData = toSignal(this.store.select(cashflowSelectors.selectCashflowData));
  cashflowType = toSignal(this.store.select(cashflowSelectors.selectCashflowType));

  cashflowView = computed(() => {
    if (
      this.cashflowType() === CASHFLOW_TYPES.budgetVsCommitGeneral ||
      this.cashflowType() === CASHFLOW_TYPES.budgetVsCommitsByProject
    ) {
      return this.cashflowViewOptions.findIndex((o) => o.value === CASHFLOW_VIEWS.COMMITMENTS);
    } else if (
      this.cashflowType() === CASHFLOW_TYPES.budgetVsPrimeGeneral ||
      this.cashflowType() === CASHFLOW_TYPES.budgetVsPrimesByProject
    ) {
      return this.cashflowViewOptions.findIndex((o) => o.value === CASHFLOW_VIEWS.PRIME_CONTRACT);
    } else if (
      this.cashflowType() === CASHFLOW_TYPES.primeVsCommitGeneral ||
      this.cashflowType() === CASHFLOW_TYPES.primeVsCommitsByProject
    ) {
      return this.cashflowViewOptions.findIndex((o) => o.value === CASHFLOW_VIEWS.PRIME_V_COMMIT);
    }
  });

  constructor(
    private store: Store,
    private cashflowService: CashFlowService,
    private fiscalService: FiscalService,
    public user: CurrentUserService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(projectActions.loadProjectsSimple());

    if (this.user.isManager) {
      this.store.dispatch(propertiesActions.loadProperties({}));
    }

    combineLatest([
      this.fiscalYear$,
      this.selectedProjectId$,
      this.projects$.pipe(filter((projects) => !!projects && projects.length > 0)),
      this.user.isManager
        ? this.properties$.pipe(filter((properties) => !!properties && properties.length > 0))
        : of(null),
    ])
      .pipe(take(1))
      .subscribe(([year, projectId, projects, properties]) => {
        this.projectsInitialLoad(projects);

        if (properties) {
          this.propertiesInitialLoad(properties);
        }

        this.store.dispatch(
          cashflowActions.filtersChanged({
            filters: {
              ...this.filters(),
              projectIds: this.isViewProject ? [projectId] : projects.map((p) => p.id),
              propertyIds: this.isViewProject ? [] : properties?.map((p) => p.id),
              year,
            },
            cashflowType: this.findCashflowTypeForSelectedOption(this.cashflowViewOptions[0].value),
          }),
        );
      });
  }

  private projectsInitialLoad(projects: ISimpleProject[]) {
    this.allProjects = DeepCopyService.deepCopy(projects);
    this.projectOptions = this.allProjects.map((project) => ({
      value: project.id,
      label: project.title,
      data: project,
    }));
  }

  private propertiesInitialLoad(properties: IProperty[]) {
    const allProperties = DeepCopyService.deepCopy(properties).toSorted((a, b) => {
      const aField = a.name.replace(specialCharReplaceRegexp, '');
      const bField = b.name.replace(specialCharReplaceRegexp, '');
      return aField.localeCompare(bField);
    });
    this.propertyOptions = allProperties.map((property) => ({
      value: property.id,
      label: property.name,
      data: property,
    }));
  }

  onCashflowViewChange(selectedOptionId: number) {
    this.store.dispatch(
      cashflowActions.filtersChanged({
        cashflowType: this.findCashflowTypeForSelectedOption(selectedOptionId),
      }),
    );
  }

  findCashflowTypeForSelectedOption(selectedOptionId: number) {
    if (selectedOptionId === CASHFLOW_VIEWS.COMMITMENTS) {
      if (this.isViewProject) {
        return CASHFLOW_TYPES.budgetVsCommitsByProject;
      } else {
        return CASHFLOW_TYPES.budgetVsCommitGeneral;
      }
    } else if (selectedOptionId === CASHFLOW_VIEWS.PRIME_CONTRACT) {
      if (this.isViewProject) {
        return CASHFLOW_TYPES.budgetVsPrimesByProject;
      } else {
        return CASHFLOW_TYPES.budgetVsPrimeGeneral;
      }
    } else {
      if (this.isViewProject) {
        return CASHFLOW_TYPES.primeVsCommitsByProject;
      } else {
        return CASHFLOW_TYPES.primeVsCommitGeneral;
      }
    }
  }

  onSearchTextChange(text: string) {
    this.store.dispatch(
      cashflowActions.filtersChanged({
        filters: { ...this.filters(), searchText: text || undefined },
      }),
    );
  }

  onFiscalYearChange(year: number) {
    this.store.dispatch(
      cashflowActions.filtersChanged({
        filters: { ...this.filters(), year },
      }),
    );
  }

  onProjectChange(projectIds: number[]) {
    this.store.dispatch(
      cashflowActions.filtersChanged({
        filters: { ...this.filters(), projectIds },
      }),
    );
  }

  onPropertyChange(propertyIds: number[]) {
    if (!propertyIds) {
      return;
    }
    if (!propertyIds.length) {
      this.projectOptions = this.allProjects.map((project) => {
        return { value: project.id, label: project.title, data: project };
      });
    } else {
      // filter shown projects by selected property
      this.projectOptions = this.allProjects
        .filter((project) => propertyIds.includes(project.property_id) || project.id === -1)
        .map((project) => {
          return { value: project.id, label: project.title, data: project };
        });
    }

    this.store.dispatch(
      cashflowActions.filtersChanged({
        filters: {
          ...this.filters(),
          projectIds: this.projectOptions.map((p) => p.value),
          propertyIds: propertyIds || [],
        },
      }),
    );
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
